import { ErrorBoundary } from '@sixfold/app-utils';
import { Button, Stack, Table, TableColumns, Text } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavLink, useNavigate } from 'react-router';

import { VehiclesMap } from './vehicles_map';
import { ConfirmButton } from '../../components/confirm_button';
import { DataToList } from '../../components/data_to_list';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { ErrorPlaceholder } from '../../components/error_placeholder';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { formatDateWithTimeZone, formattedDuration } from '../../lib/date';
import { DateTime } from '../../lib/graphql';
import { MapSlider } from '../../map/components/map_slider';
import { Routes } from '../../routes';
import {
  BreakType,
  LatestVehicleTemperature,
  Vehicle,
  VehicleBreakHistory,
  VehicleHistory,
  VehicleStatus,
  VehicleTracker,
} from '../entities';
import { isMobileTracker } from '../utils';

export interface VehicleViewDataProps {
  data: {
    vehicle?:
      | (Vehicle & {
          company: NonNullable<Vehicle['company']>;
          status: VehicleStatus | null;
          vehicleTrackers: VehicleTracker[];
          created_at: DateTime | null;
          updated_at: DateTime | null;
        })
      | null;
    history: VehicleHistory[];
    breakHistory: VehicleBreakHistory[];
  };
}

type Props = {
  deleteVehicle: (vehicleId: string) => void;
  timeline: {
    selectedTimestamp: string | undefined;
    startTime: string;
    endTime: string;
  };
} & VehicleViewDataProps;

const FormattedBreakDuration: React.FC<{ break: VehicleBreakHistory | undefined; earliestTime: string | Date }> = ({
  break: brk,
  earliestTime,
}) => {
  if (brk === undefined) {
    return <React.Fragment>{formattedDuration(earliestTime)}</React.Fragment>;
  }

  return (
    <React.Fragment>
      {brk.to === null ? 'Ongoing for' : ''} {formattedDuration(brk.to !== null ? brk.to : brk.from)}
    </React.Fragment>
  );
};

const BreaksInfo: React.FC<VehicleViewDataProps> = (props) => {
  const { breakHistory } = props.data;

  const breaksNewestToOldest = [...breakHistory].reverse();
  const latestBreak = breaksNewestToOldest[0];
  const latestRest = breaksNewestToOldest.find(
    ({ type }) => type === BreakType.rest || type === BreakType.weekendBreak,
  );
  const latestWeekendBreak = breaksNewestToOldest.find(({ type }) => type === BreakType.weekendBreak);
  const earliestBreak = breaksNewestToOldest[breaksNewestToOldest.length - 1];
  const { timeZone } = useTimeZone();

  if (breaksNewestToOldest.length === 0) {
    return <span>No breaks</span>;
  }

  return (
    <React.Fragment>
      <h4
        className="vehicle__stats_header"
        style={{ textAlign: 'center', fontSize: '0.8em', textTransform: 'uppercase', marginTop: 0 }}>
        Time since last
      </h4>
      <div
        className="statistics"
        style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', flexShrink: 0 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2em', fontWeight: 700 }}>
            <FormattedBreakDuration break={latestBreak} earliestTime={earliestBreak.from} />
          </div>
          <div style={{ fontSize: '0.75em', textTransform: 'uppercase', color: 'var(--color-neutral-text-subtle)' }}>
            Break
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2em', fontWeight: 700 }}>
            <FormattedBreakDuration break={latestRest} earliestTime={earliestBreak.from} />
          </div>
          <div style={{ fontSize: '0.75em', textTransform: 'uppercase', color: 'var(--color-neutral-text-subtle)' }}>
            Rest break
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2em', fontWeight: 700 }}>
            <FormattedBreakDuration break={latestWeekendBreak} earliestTime={earliestBreak.from} />
          </div>
          <div style={{ fontSize: '0.75em', textTransform: 'uppercase', color: 'var(--color-neutral-text-subtle)' }}>
            Weekend break
          </div>
        </div>
      </div>
      <div className="vehicle__breaks-list">
        {breaksNewestToOldest.map(({ breakId, type, from, to, formattedDuration }, idx) => (
          <div
            key={idx}
            className="item"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5em', padding: '4px 0' }}>
            <i
              className={classnames({
                'icon circle': true,
                'icon-break--small-break': type === BreakType.break,
                'icon-break--rest-break': type === BreakType.rest,
                'icon-break--we-break': type === BreakType.weekendBreak,
              })}
            />
            <div>
              <div>{`${type} #${breakId} (${formattedDuration})`}</div>
              <div>{`${formatDateWithTimeZone(from, timeZone)} - ${
                to !== null ? formatDateWithTimeZone(to, timeZone) : 'ongoing'
              } (${timeZone})`}</div>
            </div>
          </div>
        ))}
      </div>
    </React.Fragment>
  );
};

type VehicleInfoRow = {
  id: string;
  label: React.ReactNode;
  value: React.ReactNode;
};

const vehicleInfoLabelValueColumns: TableColumns<VehicleInfoRow> = [
  { id: 'label', title: undefined, width: '30%' },
  { id: 'value', title: undefined, cellContent: ({ value }) => value },
];

type SensorReadingRow = {
  id: string;
  compartmentId: LatestVehicleTemperature['sensorReadings'][0]['compartmentId'];
  sensorId: LatestVehicleTemperature['sensorReadings'][0]['sensorId'];
  temperatureCelsius: LatestVehicleTemperature['sensorReadings'][0]['temperatureCelsius'];
};

const sensorReadingColumns: TableColumns<SensorReadingRow> = [
  { id: 'compartmentId', title: 'Compartment ID' },
  { id: 'sensorId', title: 'Sensor ID' },
  { id: 'temperatureCelsius', title: 'Value °C' },
];

const TemperatureEntry: React.FC<{ vehicleTemperature: LatestVehicleTemperature }> = ({ vehicleTemperature }) => {
  const [expanded, setExpanded] = useState(false);
  const sensorData: SensorReadingRow[] = vehicleTemperature.sensorReadings.map((sr, idx) => ({
    id: String(idx),
    compartmentId: sr.compartmentId,
    sensorId: sr.sensorId,
    temperatureCelsius: sr.temperatureCelsius,
  }));

  return (
    <Stack direction="column" gap="small">
      <Stack direction="row" gap="medium" alignItems="center">
        <Button
          size="small"
          hideLabel
          iconStart={expanded ? 'small-chevron-up' : 'small-chevron-down'}
          onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Collapse' : 'Expand'}
        </Button>
        <Text size="small">
          <strong>Reefer:</strong> {vehicleTemperature.reeferId}
        </Text>
        <Text size="small">
          <strong>Timestamp:</strong> <FormattedDate date={vehicleTemperature.timestamp} />
        </Text>
        <Text size="small">
          <strong>Event Level:</strong> {vehicleTemperature.visibilitySource.eventLevel}
        </Text>
        <Text size="small">
          <strong>Event Source:</strong> {vehicleTemperature.visibilitySource.primaryEventSource}
        </Text>
        <Text size="small">
          <strong>Event Origin:</strong> {vehicleTemperature.visibilitySource.eventOrigin ?? '-'}
        </Text>
      </Stack>
      {expanded && <Table data={sensorData} columns={sensorReadingColumns} />}
    </Stack>
  );
};

export const VehicleView: React.FC<Props> = (props) => {
  const { vehicle, history, breakHistory } = props.data;
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const hasVehiclesEditPermission = useHasScopes([BackofficeScope.companyWriteVehicles]);
  const isPrivilegedUser = isAdmin || hasVehiclesEditPermission;

  if (isNil(vehicle)) {
    return <React.Fragment>Vehicle not found</React.Fragment>;
  }

  const vehicleStatusTimestamp = vehicle.status?.timestamp;
  const latestVehicleTemperatures = vehicle.latestVehicleTemperatures ?? [];

  const vehicleInfoRows: VehicleInfoRow[] = [
    { id: 'license-plate', label: 'License Plate', value: vehicle.license_plate_number ?? '-' },
    {
      id: 'company',
      label: 'Company',
      value: (
        <NavLink to={Routes.Company.generatePath({ company_id: vehicle.company.company_id })}>
          {vehicle.company.company_name ?? '-'}
        </NavLink>
      ),
    },
    {
      id: 'last-status',
      label: 'Last status update',
      value: <FormattedDate date={vehicleStatusTimestamp} />,
    },
    {
      id: 'global-pool',
      label: 'Is in global vehicle pool',
      value: vehicle.inGlobalPool?.toString() ?? 'unknown',
    },
    { id: 'is-active', label: 'Is active', value: vehicle.isActive?.toString() ?? '-' },
    { id: 'created-at', label: 'Created at', value: <FormattedDate date={vehicle.created_at} /> },
    { id: 'updated-at', label: 'Updated at', value: <FormattedDate date={vehicle.updated_at} /> },
  ];

  return (
    <div className="ui grid vehicle">
      <Helmet title={vehicle.license_plate_number ?? `Vehicle #${vehicle.vehicle_id}`} />
      <div className="row">
        <div className="height wide column vehicle__details">
          <div className="ui large header">Vehicle #{vehicle.vehicle_id}</div>
          <Stack direction="row" gap="small" style={{ marginBlock: '0.75rem' }}>
            <Button
              kind="primary"
              size="small"
              onClick={() =>
                navigate(
                  `${Routes.Tours.generatePath({})}?vehicle_id=${vehicle.vehicle_id}&carrier_id=${vehicle.company.company_id}`,
                )
              }>
              Tours with this vehicle
            </Button>
            <ConfirmButton
              initialButtonColor="red"
              label="Delete this vehicle"
              className="small"
              disabled={!isPrivilegedUser}
              onConfirm={() => props.deleteVehicle(vehicle.vehicle_id)}
            />
          </Stack>
          <Table data={vehicleInfoRows} columns={vehicleInfoLabelValueColumns} />
          <div>
            <h3>Vehicle Trackers</h3>
            {vehicle.vehicleTrackers.length === 0 ? (
              <p>No vehicle trackers</p>
            ) : (
              vehicle.vehicleTrackers.map((vehicleTracker, idx) => {
                const trackerRows: VehicleInfoRow[] = [
                  { id: 'tracker-id', label: 'Tracker ID', value: vehicleTracker.vehicleTrackerId },
                  { id: 'tracker-type', label: 'Tracker Type', value: vehicleTracker.trackerType },
                  {
                    id: 'remote-id',
                    label: 'Remote ID',
                    value: isMobileTracker(vehicleTracker) ? vehicleTracker.mobileRemoteId : vehicleTracker.fmsRemoteId,
                  },
                  {
                    id: 'integration',
                    label: 'Integration',
                    value: isMobileTracker(vehicleTracker) ? (
                      'Mobile Phone'
                    ) : vehicleTracker.telematicsIntegration !== null ? (
                      <NavLink
                        to={Routes.CompanyTelematicsIntegration.generatePath({
                          company_id: vehicle.company.company_id,
                          telematics_integration_id: vehicleTracker.telematicsIntegration?.telematicsIntegrationId,
                        })}>
                        {vehicleTracker.telematicsIntegration?.telematicsProvider.telematicsProviderName}
                      </NavLink>
                    ) : (
                      '–'
                    ),
                  },
                  {
                    id: 'tracker-data',
                    label: 'Tracker data',
                    value:
                      vehicleTracker.trackerData !== null ? (
                        <DataToList data={vehicleTracker.trackerData}>
                          {({ key, value }) => (
                            <div>
                              {key}: {value}
                            </div>
                          )}
                        </DataToList>
                      ) : (
                        '-'
                      ),
                  },
                  {
                    id: 'created-at',
                    label: 'Created at',
                    value: <FormattedDate date={vehicleTracker.createdAt} />,
                  },
                  {
                    id: 'updated-at',
                    label: 'Updated at',
                    value: <FormattedDate date={vehicleTracker.updatedAt} />,
                  },
                ];
                return <Table key={idx} data={trackerRows} columns={vehicleInfoLabelValueColumns} />;
              })
            )}
          </div>
          <div className="vehicle__details__temperature">
            <h3>Latest Vehicle Temperatures</h3>
            {latestVehicleTemperatures.length === 0 ? (
              <p style={{ textAlign: 'center' }}>No temperatures</p>
            ) : (
              <Stack direction="column" gap="medium">
                {latestVehicleTemperatures.map((vt, idx) => (
                  <TemperatureEntry key={idx} vehicleTemperature={vt} />
                ))}
              </Stack>
            )}
          </div>
          <div className="ui divider" />
          <BreaksInfo data={props.data} />
        </div>
        <div className="height wide column">
          <ErrorBoundary renderError={(error) => <ErrorPlaceholder error={error} />}>
            <MapSlider
              initialTimestamp={props.timeline.selectedTimestamp}
              timelineBoundaries={{
                startTime: props.timeline.startTime,
                endTime: props.timeline.endTime,
              }}
              vehicles={[
                {
                  history,
                  breakHistory,
                  licensePlateNumber: vehicle.license_plate_number,
                  vehicleId: vehicle.vehicle_id,
                },
              ]}>
              {({ selectedPositions }) => (
                <VehiclesMap
                  data={{
                    vehicles: [
                      {
                        ...vehicle,
                        breakHistory,
                        history,
                        ghost: selectedPositions[0],
                      },
                    ],
                  }}
                />
              )}
            </MapSlider>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};
